import React from 'react';
import { Card, CardContent, Typography, Box, Grid, Stack } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';

// Register Chart.js elements
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Status color and label configuration
const STATUS_CONFIG = [
    { id: 'complete', label: 'Complete', color: '#10B981' },
    { id: 'delivering', label: 'Delivering', color: '#3B82F6' },
    { id: 'ready_to_deliver', label: 'Ready to Deliver', color: '#FB923C' },
    { id: 'confirm', label: 'Confirm', color: '#38BDF8' },
    { id: 'submitted', label: 'Submitted', color: '#8B5CF6' },
    { id: 'cancel', label: 'Cancel', color: '#EF4444' },
];

// Sample default dataset matching the chart visual
const DEFAULT_DATA = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: {
        cancel: [100, 150, 180, 200, 220, 0],
        submitted: [5500, 6200, 6800, 7500, 8200, 0],
        confirm: [2400, 3100, 3300, 3600, 4100, 0],
        ready_to_deliver: [2300, 2600, 2700, 2900, 3000, 0],
        delivering: [600, 700, 500, 600, 500, 0],
        complete: [9800, 10000, 9800, 10100, 11000, 0],
    },
};

export default function OrderTrendChart({ data = DEFAULT_DATA, customColors = {} }) {
    const labels = Array.isArray(data?.labels) ? data.labels : DEFAULT_DATA.labels;
    const datasetsData = data?.datasets || DEFAULT_DATA.datasets;

    // Order from bottom to top in stack: Cancel -> Submitted -> Confirm -> Ready to Deliver -> Delivering -> Complete
    const stackOrder = ['cancel', 'submitted', 'confirm', 'ready_to_deliver', 'delivering', 'complete'];

    const chartDatasets = stackOrder.map((statusId) => {
        const config = STATUS_CONFIG.find((s) => s.id === statusId);
        return {
            label: config.label,
            data: Array.isArray(datasetsData[statusId]) ? datasetsData[statusId] : [],
            backgroundColor: customColors[statusId] || config.color,
            maxBarThickness: 28,
            borderRadius: 4,
        };
    });

    const chartData = {
        labels,
        datasets: chartDatasets,
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Custom legend rendered on the right
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (context) => ` ${context.dataset.label}: ${context.raw.toLocaleString()}`,
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: false,
                },
                ticks: {
                    font: { size: 12, weight: 500 },
                },
            },
            y: {
                stacked: true,
                beginAtZero: true, // Forces scale to start at 0 while auto-calculating max
                ticks: {
                    font: { size: 12 },
                    callback: (value) => {
                        if (value === 0) return '0';
                        // Automatically formats values (e.g. 500 -> 500, 15000 -> 15K, 2500000 -> 2.5M)
                        return new Intl.NumberFormat('en', {
                            notation: 'compact',
                            compactDisplay: 'short',
                            maximumFractionDigits: 1,
                        }).format(value);
                    },
                },
                grid: {
                    color: '#f6f3f3',
                },
                border: {
                    dash: [4, 4],
                    display: false,
                },
            }
        },
    };

    return (
        <Card
            sx={{
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent
                sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    '&:last-child': { pb: 3 },
                }}
            >
                {/* Title */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 3 }}>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, fontSize: '1.125rem' }}
                    >
                        Order Trend
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                    >
                        (By Status)
                    </Typography>
                </Box>

                <Grid container spacing={3} alignItems="center" sx={{ flexGrow: 1 }}>
                    {/* Stacked Bar Chart */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ height: 280, width: '100%' }}>
                            <Bar data={chartData} options={chartOptions} />
                        </Box>
                    </Grid>

                    {/* Right Legend */}
                    <Grid item xs={12} md={3}>
                        <Stack spacing={2.2}>
                            {STATUS_CONFIG.map((status) => {
                                const color = customColors[status.id] || status.color;
                                return (
                                    <Box
                                        key={status.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                bgcolor: color,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {status.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}